'use client';

import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { BrandFacetViewModel, CategoryFacetViewModel, ColorFacetViewModel, DynamicFacetKeyViewModel, DynamicFacetViewModel, FacetValueViewModel, FacetsViewModel, PriceFacetViewModel, ProductListSort } from '@/types/api';
import { PRODUCT_QUERY_PARAMS } from '@/features/navigation/constants';
import { ACTIVE_FACET_COUNT, EXCLUDED_FACETS_KEYS, STOCK_FACET_KEY } from '@/features/filter/constants';
import { ActiveFacetsProps, SetFacetProps } from '@/features/filter/types';
import { SetPriceFacetProps } from '@/features/filter/types';
import { getSelectedCategoryItems, setCategoriesSelectedProp } from '@/features/filter/utils/category-helpers';
import { PLPPages, PLPSlug } from '@/app/(plp)/types';
import { PLPFacetsContext } from './facets-context';
interface PLPFacetsProviderProps extends PropsWithChildren {
  page: PLPPages;
  searchParams?: {
    [key: string]: string | string[] | undefined | null;
  };
  slug?: PLPSlug;
}
export function PLPFacetsProvider({
  children,
  page,
  searchParams,
  slug
}: PLPFacetsProviderProps) {
  const windowSearchParams = useSearchParams();
  const [facets, setFacets] = useState<FacetsViewModel>();
  const [isApplyingFacets, setIsApplyingFacets] = useState(false);

  // filters other than facets
  const [onlyInStock, setOnlyInStock] = useState(searchParams?.[PRODUCT_QUERY_PARAMS.HAS_STOCK] === 'true');
  const [pageNumber, setPageNumber] = useState(() => parseInt(String(searchParams?.[PRODUCT_QUERY_PARAMS.PAGE_NUMBER])) || undefined);
  const [sortBy, setSortBy] = useState(() => searchParams?.[PRODUCT_QUERY_PARAMS.SORT_BY] as ProductListSort || undefined);

  // this state stores a boolean which indicates if user any filter is applied or not
  // due to costly loops and nested filters, we decided not to use useMemo for calculating this state
  const [anyFiltersApplied, setAnyFiltersApplied] = useState<boolean | undefined>(undefined);

  // True if filter change was triggered by user, not URL, Using ref to avoid unnecessary re-renders.
  const filterChangeByUserRef = useRef(false);
  useEffect(() => {
    const paramsPageNumber = windowSearchParams.get(PRODUCT_QUERY_PARAMS.PAGE_NUMBER);
    if (paramsPageNumber && !isNaN(+paramsPageNumber) && +paramsPageNumber !== pageNumber) {
      setPageNumber(+paramsPageNumber);
    }
    if ((!paramsPageNumber || isNaN(+paramsPageNumber)) && pageNumber !== 1) {
      setPageNumber(undefined);
    }
  }, [windowSearchParams, pageNumber]);
  const pageFacets = useMemo(() => {
    const result: Partial<FacetsViewModel> = {};
    if (!facets || !page) return result;
    const filter = EXCLUDED_FACETS_KEYS[page];
    Object.entries(facets).forEach(([facetKey, facetValues]) => {
      const key = facetKey as keyof FacetsViewModel;
      if (!filter.includes(key)) result[key] = facetValues;
    });
    return result;
  }, [facets, page]);
  const activeFacets = useMemo(() => {
    let filtersApplied = false;
    if (!facets) return null;
    let count = 0;
    const result: ActiveFacetsProps = {};
    Object.entries(facets).forEach(([facetKey, facetValue]) => {
      if (facetKey === 'dynamicFacets') {
        result.dynamicFacets = [];
        if (!facetValue) return;
        const dynamicFacets = facetValue as DynamicFacetViewModel[];
        dynamicFacets.forEach(({
          values
        }) => {
          values?.forEach(innerFacet => {
            if (innerFacet.isSelected) (result.dynamicFacets as FacetValueViewModel[]).push(innerFacet);
          });
        });
        if (result.dynamicFacets.length > 0) {
          filtersApplied = true;
          count += result.dynamicFacets.length;
        }
      } else if (Array.isArray(facetValue) && facetValue.length && typeof facetValue[0].isSelected !== 'undefined') {
        if (facetKey === 'categories') {
          result[facetKey] = getSelectedCategoryItems(facetValue);
          if (result[facetKey].length) {
            filtersApplied = true;
            count += result[facetKey].length;
          }
        } else {
          const currentFacets: null | ColorFacetViewModel[] | BrandFacetViewModel[] | CategoryFacetViewModel[] = facetValue;
          if (!currentFacets) return;
          result[facetKey] = currentFacets.filter(f => f.isSelected);
          if (result[facetKey].length) {
            filtersApplied = true;
            count += result[facetKey].length;
          }
        }
      } else {
        result[facetKey] = facetValue;
        if (facetKey as keyof FacetsViewModel === 'priceFacet') {
          const priceFacet = facetValue as PriceFacetViewModel;
          if (priceFacet?.selectedFromPrice || priceFacet?.selectedToPrice) {
            filtersApplied = true;
            count++;
          }
        }
      }
    });
    if (onlyInStock) {
      result[STOCK_FACET_KEY] = `true`;
      filtersApplied = true;
      count++;
    }
    setAnyFiltersApplied(filtersApplied);
    result[ACTIVE_FACET_COUNT] = count;
    return result;
  }, [facets, onlyInStock]);
  const setFacetsValues = useCallback(({
    mainFacetKey,
    innerKey,
    value
  }: SetFacetProps | SetPriceFacetProps) => {
    filterChangeByUserRef.current = true;
    const newFacets: FacetsViewModel = facets ? {
      ...facets
    } : {};
    switch (mainFacetKey) {
      case 'brandFacets':
        {
          const newValue = value as BrandFacetViewModel[];
          newFacets.brandFacets = (newFacets.brandFacets || []).map(b => {
            const selectedBrand = newValue.find(v => v.key === b.key);
            return selectedBrand || {
              ...b,
              isSelected: false
            };
          });
          setFacets(newFacets);
        }
        break;
      case 'colorFacets':
        {
          const newValues = value as ColorFacetViewModel[];
          newFacets.colorFacets = (newFacets.colorFacets || []).map(c => {
            const selectedColor = newValues.find(v => (typeof v === 'string' ? v : v.key) === c.key);
            return selectedColor ? {
              ...c,
              isSelected: true
            } : {
              ...c,
              isSelected: false
            };
          });
          setFacets(newFacets);
        }
        break;
      case 'priceFacet':
        {
          const priceFacet = {
            ...newFacets.priceFacet,
            ...value
          };
          newFacets.priceFacet = priceFacet;
          setFacets(newFacets);
        }
        break;
      case 'dynamicFacets':
        {
          const newDynamicFacets: DynamicFacetViewModel[] = (newFacets.dynamicFacets || []).map((innerDF: DynamicFacetViewModel) => {
            // if key doesn't match current innerDF, don't proceed. go for next item int the list
            if (innerDF.key?.displayNameEn !== (innerKey as DynamicFacetKeyViewModel).displayNameEn) return innerDF;
            return {
              ...innerDF,
              values: (innerDF.values || []).map(v => {
                const newValue = value as FacetValueViewModel[];
                const itemWithNewValue = newValue.find(nv => nv.key === v.key);
                return itemWithNewValue || {
                  ...v,
                  isSelected: false
                };
              })
            };
          });
          setFacets({
            ...newFacets,
            dynamicFacets: newDynamicFacets
          });
        }
        break;
      case 'categories':
        setFacets({
          ...facets,
          categories: value as CategoryFacetViewModel[]
        });
        break;
    }
  }, [facets]);
  const facetsSearchParams = useMemo(() => {
    const searchParams = new URLSearchParams(windowSearchParams.toString());
    if (activeFacets) Object.entries(activeFacets).forEach(([key, value]) => {
      if (typeof value === 'string') {
        if (key === STOCK_FACET_KEY && value === 'true') {
          searchParams.append(PRODUCT_QUERY_PARAMS.HAS_STOCK, value);
        }
        return;
      }
      switch (key as keyof FacetsViewModel) {
        case 'brandFacets':
          searchParams.delete(PRODUCT_QUERY_PARAMS.BRAND);
          Array.isArray(value) && value.forEach((v: BrandFacetViewModel) => v.key && searchParams.append(PRODUCT_QUERY_PARAMS.BRAND, v.key));
          break;
        case 'categories':
          searchParams.delete(PRODUCT_QUERY_PARAMS.CATEGORY);
          Array.isArray(value) && value.forEach((v: CategoryFacetViewModel) => v.key && searchParams.append(PRODUCT_QUERY_PARAMS.CATEGORY, v.key));
          break;
        case 'colorFacets':
          searchParams.delete(PRODUCT_QUERY_PARAMS.COLOR);
          Array.isArray(value) && value.forEach((v: ColorFacetViewModel) => v.key && searchParams.append(PRODUCT_QUERY_PARAMS.COLOR, v.key));
          break;
        case 'dynamicFacets':
          searchParams.delete(PRODUCT_QUERY_PARAMS.DYNAMIC_FACET);
          Array.isArray(value) && value.forEach((v: CategoryFacetViewModel) => v.key && searchParams.append(PRODUCT_QUERY_PARAMS.DYNAMIC_FACET, v.key));
          break;
        case 'stockFacet':
          searchParams.delete(PRODUCT_QUERY_PARAMS.HAS_STOCK);
          if (typeof value === 'string' && value === 'true') searchParams.append(PRODUCT_QUERY_PARAMS.HAS_STOCK, 'true');
          break;
        case 'priceFacet':
          {
            searchParams.delete(PRODUCT_QUERY_PARAMS.PRICE_FROM);
            searchParams.delete(PRODUCT_QUERY_PARAMS.PRICE_TO);
            const priceFacet = value as PriceFacetViewModel;
            if (priceFacet?.selectedFromPrice) searchParams.append(PRODUCT_QUERY_PARAMS.PRICE_FROM, `${priceFacet.selectedFromPrice}`);
            if (priceFacet?.selectedToPrice) searchParams.append(PRODUCT_QUERY_PARAMS.PRICE_TO, `${priceFacet.selectedToPrice}`);
          }
          break;
      }
    });
    pageNumber ? searchParams.set(PRODUCT_QUERY_PARAMS.PAGE_NUMBER, String(pageNumber)) : searchParams.delete(PRODUCT_QUERY_PARAMS.PAGE_NUMBER);
    sortBy && searchParams.set(PRODUCT_QUERY_PARAMS.SORT_BY, sortBy);
    const initialSearchParamsStr = windowSearchParams.toString();
    const finalSearchParamsStr = searchParams.toString();

    // Compare initial and final search params, ignoring 'page number' and parameter order.
    // If filters have changed, remove the page number param to reset pagination.
    if (finalSearchParamsStr !== initialSearchParamsStr) {
      const initialParamsNoPageNumber = new URLSearchParams(initialSearchParamsStr);
      initialParamsNoPageNumber.sort();
      initialParamsNoPageNumber.delete(PRODUCT_QUERY_PARAMS.PAGE_NUMBER);
      const finalParamsNoPageNumber = new URLSearchParams(finalSearchParamsStr);
      finalParamsNoPageNumber.sort();
      finalParamsNoPageNumber.delete(PRODUCT_QUERY_PARAMS.PAGE_NUMBER);
      if (initialParamsNoPageNumber.toString() !== finalParamsNoPageNumber.toString()) {
        searchParams.delete(PRODUCT_QUERY_PARAMS.PAGE_NUMBER);
      }
    }
    return searchParams;
  }, [activeFacets, pageNumber, sortBy, windowSearchParams]);
  const deactivateFacets = useCallback((keys: (keyof FacetsViewModel | string)[]) => {
    let deselectedItems: Partial<FacetsViewModel> = {
      ...facets
    };
    keys.forEach(key => {
      switch (key) {
        case 'categories':
          {
            if (!facets?.categories) return;
            const clonedList = setCategoriesSelectedProp(structuredClone(facets.categories), false);
            deselectedItems = {
              ...deselectedItems,
              categories: clonedList
            };
          }
          break;
        case 'dynamicFacets':
          {
            const deselectedDynamicFacets = facets?.dynamicFacets?.map(dynamicFacet => ({
              ...dynamicFacet,
              values: dynamicFacet.values?.map(innerDF => ({
                ...innerDF,
                isSelected: false
              }))
            })) || [];
            deselectedItems = {
              ...deselectedItems,
              [key]: deselectedDynamicFacets
            };
          }
          break;
        case 'priceFacet':
          deselectedItems = {
            ...deselectedItems,
            [key]: {
              ...facets?.[key],
              ...{
                selectedFromPrice: null,
                selectedToPrice: null
              }
            }
          };
          break;
        case 'brandFacets':
        case 'colorFacets':
          {
            const collection: ColorFacetViewModel[] | BrandFacetViewModel[] = facets?.[key] || [];
            const deselectedFacetsList = collection.map(facet => ({
              ...facet,
              isSelected: false
            }));
            deselectedItems = {
              ...deselectedItems,
              [key]: deselectedFacetsList
            };
          }
          break;
      }
    });
    setFacets(deselectedItems);
    if (keys.includes(STOCK_FACET_KEY)) setOnlyInStock(false);
  }, [facets]);
  const contextValue = useMemo(() => ({
    slug,
    allFacets: facets,
    setFacetsValues,
    setOnlyInStock,
    activeFacets,
    facetsSearchParams,
    anyFiltersApplied,
    deactivateFacets,
    pageFacets,
    setPageNumber,
    pageNumber,
    setSortBy,
    page,
    searchParams,
    setIsApplyingFacets,
    isApplyingFacets,
    onlyInStock,
    filterChangeByUserRef,
    syncFacets: setFacets
  }), [activeFacets, anyFiltersApplied, deactivateFacets, facets, facetsSearchParams, isApplyingFacets, onlyInStock, page, pageFacets, pageNumber, searchParams, setFacetsValues, slug]);
  return <PLPFacetsContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="PLPFacetsProvider" data-sentry-source-file="facets-provider.tsx">{children}</PLPFacetsContext.Provider>;
}